import { ConnectedRouter } from 'connected-next-router';
import { useRouter } from 'next/router';
import { memo, type ReactNode, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { usePrevious, useUnmount } from 'react-use';

import { useAnalyticsUtilities, useTrackViewedBoard } from '@air/analytics';
import { ActionBarProvider } from '@air/provider-action-bar';
import { ModalProvider, useAirModal } from '@air/provider-modal';
import { ShortIdProvider, useShortIdContext } from '@air/provider-short-id';
import { useToasts } from '@air/provider-toast';
import { ErrorBoundary } from '@air/utils-error';

import { AccountProvisionerProvider } from '~/components/AccountProvisioner/providers/AccountProvisionerProvider';
import { usePublicPageViewNotification } from '~/components/AirNotifications/hooks/usePublicPageViewNotification';
import { PublicFiltersReduxObserver } from '~/components/Filters/PublicFiltersReduxObserver';
import { usePublicBoardPage } from '~/components/Layouts/PublicBoardLayout/hooks/usePublicBoardPage';
import { PublicBoardClipSEO } from '~/components/Layouts/PublicBoardLayout/PublicBoardClipSEO';
import { PublicBoardSEO } from '~/components/Layouts/PublicBoardLayout/PublicBoardSEO';
import { ViewableBoard } from '~/components/Layouts/PublicBoardLayout/ViewableBoard';
import { MaintenanceMode } from '~/components/MaintenanceMode/MaintenanceMode';
import { PublicBoardReduxProvider } from '~/components/PublicBoardReduxProvider';
import { PublicBoardSocketContextProvider } from '~/components/PublicBoardSocketContextProvider';
import { PublicBoardAnonymousSignUpModal } from '~/components/PublicLinkAnonymousSignUpModal/PublicBoardAnonymousSignUpModal';
import { PublicPermissionsProvider } from '~/components/PublicPermissionsProvider';
import { PublicShareWorkspaceAccessProvider } from '~/components/PublicShareWorkspaceAccessProvider';
import { usePublicBoardSSOCallback } from '~/components/PublicSSOCallback/usePublicBoardSSOCallback';
import { PublicWorkspaceProvider } from '~/components/PublicWorkspaceProvider';
import { useUI2026Styles } from '~/components/v2/hooks/useUI2026Styles';
import { createShortUrlRoute } from '~/constants/routes';
import { useOnLocationChange } from '~/hooks/router/useOnLocationChange';
import { useOnMissingUserSession } from '~/hooks/useOnMissingUserSession';
import { usePublicUrlBoardNavigation } from '~/hooks/usePublicUrlBoardNavigation';
import { ThumbnailPreferenceContextProvider } from '~/hooks/useThumbnailPreference';
import { useURLBoardIdSelector } from '~/hooks/useURLBoardIdSelector';
import { useURLClipIdSelector } from '~/hooks/useURLClipIdSelector';
import { PublicAccountProvider, useAccountContext } from '~/providers/AccountProvider';
import { ExternalLinksClickableProvider } from '~/providers/ExternalLinksClickableProvider';
import { FiltersProvider, type GetImmutableFilters } from '~/providers/FiltersProvider';
import { ModifiersKeysPressedProvider } from '~/providers/ModifiersKeysPressedProvider';
import { PublicBoardCustomFieldsProvider } from '~/providers/PublicBoardCustomFieldsProvider';
import { PublicBoardProvider } from '~/providers/PublicBoardProvider';
import { PublicUrlAssetModalNavigationProvider } from '~/providers/PublicUrlAssetModalNavigationProvider';
import { SearchHeaderHeightProvider } from '~/providers/SearchHeaderHeightProvider';
import { resetCentralizedBoardAction } from '~/store/centralizedBoard/actions';
import {
  resetConfigViewsAction,
  setCurrentViewTypeAction,
  setSavedConfigurableViewsAction,
} from '~/store/configViews/actions';
import { currentViewTypeNameSelector, savedConfigurableViewsSelector } from '~/store/configViews/selectors';
import { useActiveClipSeoData } from '~/swr-hooks/shortUrl/useActiveClipSeoData';
import { type ClipSeoData, type PublicBoardShareData } from '~/swr-hooks/shortUrl/utils';
import { getBoardIdFromPath } from '~/utils/PathUtils';
import { useAirStore } from '~/utils/ReduxUtils';

export type PublicBoardInnerProps = Pick<PublicBoardProps, 'boardId' | 'initialData'> & {
  initialClipData?: ClipSeoData | null;
};

const PublicBoardInner = ({ initialData, boardId: boardIdFromProps, initialClipData }: PublicBoardInnerProps) => {
  const dispatch = useDispatch();
  const store = useAirStore();
  const urlBoardId = useURLBoardIdSelector();
  const boardId = urlBoardId || boardIdFromProps;
  const previousBoardId = usePrevious(boardId);
  const clipId = useURLClipIdSelector();
  const { shortId } = useShortIdContext();

  const activeClip = useActiveClipSeoData({ clipId, shortId, initialClipData });
  const { trackViewedBoard } = useTrackViewedBoard();
  const { setGlobalEventProps } = useAnalyticsUtilities();
  const { dismissAllToasts } = useToasts();

  const { data: account } = useAccountContext();

  usePublicBoardSSOCallback();

  const [showAnonymousSignupModal] = useAirModal(PublicBoardAnonymousSignUpModal);

  const { sendPublicPageViewEvent } = usePublicPageViewNotification();

  const requireAccount = initialData.requireAccount;

  useOnMissingUserSession({
    onMissingSession: () => showAnonymousSignupModal({}),
    enabled: requireAccount,
  });

  useEffect(() => {
    if (!requireAccount || (requireAccount && !!account)) {
      sendPublicPageViewEvent(shortId);
    }
  }, [account, requireAccount, sendPublicPageViewEvent, shortId]);

  const { data: board } = usePublicBoardPage({
    boardId,
    initialData: boardId === initialData.board.id ? { ...initialData.board, ancestors: [] } : undefined,
  });

  useEffect(() => {
    setGlobalEventProps({
      // workspace information whether the user is logged in or not
      workspaceId: initialData.workspace.id,
      workspaceName: initialData.workspace.name,
    });
  }, [setGlobalEventProps, initialData.workspace.id, initialData.workspace.name]);

  useEffect(() => {
    if (board?.id && !clipId) {
      trackViewedBoard({
        board_id: board.id,
        board_title: board.title,
        isDemo: board.isDemo,
        board_view: board.views?.find(({ isDefault }) => !!isDefault)?.viewType.name,
      });
    }
    // We only want to track when a user navigates to a new board (or on load)
    // so we want the id instead of the entire board object cause a board could
    // get modified in local cache (unlikely in a public board but better to be safe)
    // we watch clipId because we don't want to consider it a board view if they're
    // just looking at the asset modal
  }, [board?.id, clipId, board?.title, board?.isDemo, board?.views, trackViewedBoard]);

  useEffect(() => {
    if (previousBoardId && previousBoardId !== boardId) {
      dispatch(resetConfigViewsAction());
    }
  }, [boardId, dispatch, previousBoardId]);

  useEffect(() => {
    if (board?.views) {
      const currentViews = savedConfigurableViewsSelector(store.getState());
      const currentViewTypeName = currentViewTypeNameSelector(store.getState());

      const boardViews = board.views;
      dispatch(setSavedConfigurableViewsAction({ savedConfigurableViews: boardViews }));
      if (currentViews && currentViewTypeName) {
        dispatch(setCurrentViewTypeAction({ viewTypeName: currentViewTypeName }));
      }
    }
  }, [dispatch, board?.views, store]);

  useOnLocationChange(dismissAllToasts);

  // this can't really happen on a public board URL cause where can the user go?
  // but just for safe measure and future-proofing
  useUnmount(() => {
    dispatch(resetCentralizedBoardAction());
  });

  return (
    <>
      {clipId && activeClip ? (
        <PublicBoardClipSEO clip={activeClip} boardId={boardId} />
      ) : (
        board && <PublicBoardSEO board={board} />
      )}
      <ViewableBoard />
    </>
  );
};

export type LegacyPublicBoardBindingsProps = {
  children: ReactNode;
  shortId: string;
};

/**
 * The legacy (<medium viewport) board tree's PublicBoardProvider mount. It cannot mount v2
 * PublicBoard (which carries its own provider), so the contract is mounted directly at the tree's
 * boundary — this mount dies with the legacy tree. A component of its own (rather than a hook call
 * in PublicBoard's body) because the drill hook's centralized-board seed must dispatch into THIS
 * tree's store: it has to render below PublicBoardReduxProvider, which PublicBoard itself mounts.
 */
const LegacyPublicBoardBindings = memo(({ children, shortId }: LegacyPublicBoardBindingsProps) => {
  // `activePage: null` because board links have no sub-pages — drills carry no `?page=` trail.
  const { onNavigateToBoard } = usePublicUrlBoardNavigation({
    shortId,
    basePath: createShortUrlRoute(shortId),
    activePage: null,
  });

  return <PublicBoardProvider onNavigateToBoard={onNavigateToBoard}>{children}</PublicBoardProvider>;
});

LegacyPublicBoardBindings.displayName = 'LegacyPublicBoardBindings';

export type PublicBoardProps = {
  initialData: PublicBoardShareData;
  shortId: string;
  boardId: string;
  initialClipData?: ClipSeoData | null;
};

export const PublicBoard = memo(({ initialData, shortId, boardId, initialClipData }: PublicBoardProps) => {
  const { asPath } = useRouter();
  const clipId = useURLClipIdSelector();
  // Narrow public board never mounts PublicBoardLayout, which is the other setter.
  useUI2026Styles({ enabled: false });

  const { name: workspaceName, image: workspaceImage, id: workspaceId } = initialData.workspace;

  const getImmutableFilters = useCallback<GetImmutableFilters>(
    () => ({
      boardId: getBoardIdFromPath(asPath),
    }),
    [asPath],
  );

  return (
    <AccountProvisionerProvider>
      <PublicAccountProvider workspace={initialData.workspace}>
        <PublicPermissionsProvider permissions={initialData.permissions}>
          <PublicBoardReduxProvider
            initialBoard={initialData.board}
            presentationSettings={initialData.presentationSettings}
          >
            <ExternalLinksClickableProvider areExternalLinksClickable={initialData.isOnPaidPlan === true}>
              <ShortIdProvider
                shortId={shortId}
                objectId={initialData.board.id}
                objectType="board"
                presentationSettings={initialData.presentationSettings}
                isOnPaidPlan={initialData.isOnPaidPlan}
              >
                <PublicUrlAssetModalNavigationProvider>
                  <LegacyPublicBoardBindings shortId={shortId}>
                    <PublicWorkspaceProvider
                      workspaceId={workspaceId}
                      workspaceImage={workspaceImage}
                      workspaceName={workspaceName}
                    >
                      <PublicShareWorkspaceAccessProvider>
                        <ErrorBoundary>
                          <ConnectedRouter>
                            {/*
            SearchHeaderHeightProvider should probably be a different instance than what's in private
            because the two designs/componetns should be separate since they don't depend on each other
          */}
                            <SearchHeaderHeightProvider>
                              <FiltersProvider getImmutableFilters={getImmutableFilters}>
                                <ModifiersKeysPressedProvider>
                                  <ModalProvider>
                                    <ThumbnailPreferenceContextProvider>
                                      <MaintenanceMode>
                                        <PublicBoardCustomFieldsProvider>
                                          <PublicBoardSocketContextProvider>
                                            <ActionBarProvider>
                                              {!clipId && <PublicBoardSEO board={initialData.board} />}

                                              <PublicFiltersReduxObserver />
                                              <PublicBoardInner
                                                initialData={initialData}
                                                boardId={boardId}
                                                initialClipData={initialClipData}
                                              />
                                            </ActionBarProvider>
                                          </PublicBoardSocketContextProvider>
                                        </PublicBoardCustomFieldsProvider>
                                      </MaintenanceMode>
                                    </ThumbnailPreferenceContextProvider>
                                  </ModalProvider>
                                </ModifiersKeysPressedProvider>
                              </FiltersProvider>
                            </SearchHeaderHeightProvider>
                          </ConnectedRouter>
                        </ErrorBoundary>
                      </PublicShareWorkspaceAccessProvider>
                    </PublicWorkspaceProvider>
                  </LegacyPublicBoardBindings>
                </PublicUrlAssetModalNavigationProvider>
              </ShortIdProvider>
            </ExternalLinksClickableProvider>
          </PublicBoardReduxProvider>
        </PublicPermissionsProvider>
      </PublicAccountProvider>
    </AccountProvisionerProvider>
  );
});

PublicBoard.displayName = 'PublicBoard';
